import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createModuleStrings } from '@platform/core/lib/moduleStrings';
import type { IModuleStrings } from '@platform/core/types';
import { moduleBundle } from '@platform/i18n';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { findModuleDefinition } from './moduleDefinitionRegistry';
import { ModuleStringsContext } from './ModuleStringsContext';
import { workspaceStrings } from './workspaceStrings';

/**
 * Рядки модуля мовою інтерфейсу: з ключем — резолвер для цього модуля (перейменування простору → пакет `module.<ключ>` → підпис визначення;
 * перейменування — підписи злитого визначення, яких немає в опублікованому визначенні пакета, ADR-023);
 * без ключа — резолвер із ModuleStringsContext, а поза провайдером — чистий fallback на підписи визначення.
 * Перерендер на зміну мови та на додавання рядків пакета (useTranslation, bindI18nStore 'added'), тож рендер на i18n не чекає.
 */
export function useModuleStrings(moduleKey?: string): IModuleStrings {
  const inherited = useContext(ModuleStringsContext);
  const lang = useAppSelector(selectLang);
  useTranslation();
  return useMemo(() => {
    if (moduleKey === undefined && inherited) return inherited;
    const key = moduleKey ?? '';
    return workspaceStrings(createModuleStrings(key, lang, () => moduleBundle(lang, key)), findModuleDefinition(key));
  }, [moduleKey, inherited, lang]);
}
